import _ from 'lodash';
import React from 'react';
import {Redirect, Route, RouteProps, Switch} from 'react-router';
import App from './App';
import {ToastContainer} from './components/elements/Toast';
import GlobalNudgeBarContextProvider from './components/GlobalNudgeBarContextProvider';
import LocalNudgeBar from './components/LocalNudgeBar';
import NoMatch from './components/NoMatch';
import SearchNav from './components/SearchNav';
import StorageNudgeBar from './components/StorageNudgeBar';
import TrialNudgeBar from './components/TrialNudgeBar';
import ComputeHourNudgeBar from './components/ComputeHourNudgeBar';
import config, {envIsLocal} from './config';
import PrivateRoute, {PrivateRouteProps} from './containers/PrivateRoute';
import routesCheck, {
  BENCHMARK_PATHS,
  GALLERY_DISCUSSION_EDIT_PATH,
  GALLERY_DISCUSSION_VIEW_PATH,
  GALLERY_PATH,
  GALLERY_PATH_SEGMENT,
  GALLERY_POST_EDIT_PATH,
  GALLERY_POST_VIEW_PATH,
  GROUP_PATHS,
  ONBOARDING_FLOW_PATH,
  PROFILE_LIKES_PATH,
  PROFILE_MODELS_PATH,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROJECT_BASE_PATH,
  PROJECT_PATHS,
  QUICKSTART_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  RUN_PATHS,
  STORAGE_EXPLORER_TRACKED_PATH,
  SWEEP_PATHS,
} from './routeData';
import makeComp from './util/profiler';
import LocalLicenseNudgeBar from './components/LocalLicenseNudgeBar';
import {isInIframe} from './setup';
import {GetLicenseNudgeBar} from './components/LocalLicenseGetNudge';
import Loader from './components/WandbLoader';
import MessageOfTheDay from './components/MessageOfTheDay';
import {useViewer} from './state/viewer/hooks';

const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

interface PlainRouteData extends RouteProps {
  RouteComponent: typeof Route;
}

interface RouteWithLayoutData extends RouteWithLayoutProps {
  RouteComponent: typeof RouteWithLayout;
}

interface PrivateRouteData extends PrivateRouteProps {
  RouteComponent: typeof PrivateRoute;
}

interface RedirectData {
  path: string | string[];
  exact?: true;
  redirect: string;
}

type RouteData = (
  | PlainRouteData
  | RouteWithLayoutData
  | PrivateRouteData
  | RedirectData
) & {
  condition?: boolean;
};

export interface RouteWithLayoutProps {
  component: React.ComponentType<any>;
  error?: any;
  allowIframes?: boolean;
  additionalProps?: {[prop: string]: any};
  [prop: string]: any;
}

// adds the Nav and Footer before and after the route component
export const RouteWithLayout: React.FC<RouteWithLayoutProps> = makeComp(
  ({
    component: Component,
    error,
    allowIframes,
    additionalProps = {},
    ...rest
  }) => {
    const preventIframe = isInIframe() && !allowIframes;
    return (
      <Route
        {...rest}
        render={routeProps => {
          return preventIframe ? (
            <NoMatch unrecoverable />
          ) : (
            <Component error={error} {...routeProps} {...additionalProps} />
          );
        }}
      />
    );
  },
  {id: 'RouteWithLayout'}
);

export function makeRoutes(): RouteData[] {
  const routes = [
    {
      RouteComponent: Route,
      path: '/__test_page',
      component: React.lazy(() => import('./pages/TestPage')),
      exact: true,
    },
    {
      RouteComponent: Route,
      path: ['/__registry', '/__registry/:tab'],
      component: React.lazy(() => import('./components/Panel2/RegistryPage')),
      exact: true,
    },
    {
      RouteComponent: Route,
      path: ['/__wbench', '/__wbench/:tab'],
      component: React.lazy(
        () => import('./components/Panel2/InternalBenchmarkPage')
      ),
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/',
      component: React.lazy(() => import('./pages/Home')),
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/login',
      component: React.lazy(() => import('./components/Login')),
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/login-redirect',
      component: React.lazy(() => import('./components/LoginRedirect')),
    },
    {
      RouteComponent: Route,
      path: '/logout',
      component: React.lazy(() => import('./components/Logout')),
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/trending',
      component: React.lazy(() => import('./pages/TrendingRepos')),
    },
    {
      RouteComponent: PrivateRoute,
      path: '/home',
      component: React.lazy(() => import('./pages/HomePageNew')),
      routeCache: 'HomePage',
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/home/projects',
      component: React.lazy(() => import('./pages/AllProjects')),
      routeCache: 'AllProjectsPage',
      exact: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: QUICKSTART_PATH,
      component: React.lazy(() => import('./pages/HomeQuickStart')),
      routeCache: 'HomeQuickStart',
    },
    {
      RouteComponent: PrivateRoute,
      path: `/${GALLERY_PATH_SEGMENT}/create-post`,
      component: React.lazy(() => import('./pages/GalleryCreatePost')),
      routeCache: 'GalleryCreatePost',
    },
    {
      RouteComponent: PrivateRoute,
      path: `/${GALLERY_PATH_SEGMENT}/create-discussion`,
      component: React.lazy(() => import('./pages/GalleryCreateDiscussion')),
      routeCache: 'GalleryCreateDiscussion',
    },
    {
      RouteComponent: RouteWithLayout,
      path: GALLERY_PATH,
      component: React.lazy(() => import('./pages/GalleryPage')),
    },
    {
      path: '/fullyconnected',
      redirect: GALLERY_PATH,
    },
    {
      path: '/fc',
      redirect: GALLERY_PATH,
    },
    {
      path: '/gallery',
      redirect: GALLERY_PATH,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/__test/simple_error',
      component: React.lazy(() => import('./pages/ErrorTestPage')),
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/users/:userName',
      component: React.lazy(() =>
        import('./pages/HomePageNew').then(({HomePageForUser}) => ({
          default: HomePageForUser,
        }))
      ),
      routeCache: 'UserPage',
    },
    {
      RouteComponent: PrivateRoute,
      path: ONBOARDING_FLOW_PATH,
      component: React.lazy(() => import('./pages/OnboardingFlow')),
      routeCache: 'OnboardingFlow',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/change-password',
      component: React.lazy(() => import('./pages/ChangePassword')),
      routeCache: 'ChangePassword',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/authorize',
      component: React.lazy(() => import('./pages/AuthorizePage')),
      routeCache: 'AuthorizePage',
      allowIframes: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/settings',
      component: React.lazy(() => import('./pages/SettingsPage')),
      routeCache: 'SettingsPage',
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: ['/billing', '/subscriptions'],
      component: React.lazy(() => import('./pages/BillingPage')),
      routeCache: 'BillingPage',
      exact: true,
      condition: !isOnPrem,
    },
    {
      RouteComponent: PrivateRoute,
      path: ['/team-payment'],
      component: React.lazy(() => import('./pages/TeamPayment')),
      routeCache: 'TeamPayment',
      exact: true,
      condition: !isOnPrem,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/usage',
      component: React.lazy(() => import('./pages/UsageAdmin')),
      routeCache: 'UsageAdmin',
      exact: true,
      condition: isOnPrem,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/team',
      component: React.lazy(() => import('./pages/TeamAdmin')),
      routeCache: 'TeamAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/organization',
      component: React.lazy(() => import('./pages/OrganizationAdminPage')),
      routeCache: 'OrganizationAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/users',
      component: React.lazy(() => import('./pages/UsersAdminPage')),
      routeCache: 'UsersAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/license',
      component: React.lazy(() => import('./pages/LicenseAdminPage')),
      routeCache: 'LicenseAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: `/admin/${GALLERY_PATH_SEGMENT}`,
      component: React.lazy(() => import('./pages/GalleryAdmin')),
      routeCache: 'GalleryAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/ramp',
      component: React.lazy(() => import('./pages/RampAdmin')),
      routeCache: 'RampAdmin',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/create-team',
      component: React.lazy(() => import('./pages/CreateTeam')),
      routeCache: 'CreateTeam',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/teams/:entityName',
      component: React.lazy(() => import('./pages/Team')),
      routeCache: 'TeamPage',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/admin/home',
      component: React.lazy(() => import('./pages/AdminDashboardPage')),
      routeCache: 'AdminDashboard',
    },
    {
      RouteComponent: RouteWithLayout,
      path: [
        '/usage/:entityName',
        STORAGE_EXPLORER_TRACKED_PATH,
        '/usage/:entityName/:filePath*',
      ],
      component: React.lazy(() => import('./pages/StorageExplorer')),
      routeCache: 'StorageExplorer',
      exact: true,
      allowAnonymous: true,
    },
    {
      path: '/storage/:entityName/tracked/:filePath*',
      redirect: STORAGE_EXPLORER_TRACKED_PATH,
    },
    {
      path: '/storage/:entityName/:filePath*',
      redirect: '/usage/:entityName/:filePath*',
    },
    {
      path: '/storage/:entityName',
      redirect: '/usage/:entityName',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/new-project',
      component: React.lazy(() => import('./pages/NewProject')),
      routeCache: 'NewProject',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/new-benchmark',
      component: React.lazy(() => import('./pages/NewBenchmark')),
      routeCache: 'NewBenchmark',
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityname/benchmark/new',
      component: React.lazy(() => import('./pages/NewBenchmark')),
      routeCache: 'NewBenchmarkPage',
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROFILE_PROJECTS_PATH,
      component: React.lazy(() => import('./pages/EntityPage')),
      routeCache: 'EntityPage',
      exact: true,
      additionalProps: {tab: 'projects'},
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROFILE_LIKES_PATH,
      component: React.lazy(() => import('./pages/EntityPage')),
      routeCache: 'EntityPage',
      exact: true,
      additionalProps: {tab: 'likes'},
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROFILE_MODELS_PATH,
      component: React.lazy(() => import('./pages/EntityPage')),
      routeCache: 'EntityPage',
      exact: true,
      additionalProps: {tab: 'models'},
    },
    {
      RouteComponent: PrivateRoute,
      path: RUN_PATHS,
      component: React.lazy(() => import('./pages/RunPage2')),
      routeCache: 'RunPage2',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: GROUP_PATHS,
      component: React.lazy(() => import('./pages/GroupPage')),
      routeCache: 'GroupPage',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/create-sweep',
      component: React.lazy(() => import('./pages/CreateSweepPage')),
      routeCache: 'CreateSweepPage',
      allowIframes: true,
      exact: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: SWEEP_PATHS,
      component: React.lazy(() => import('./pages/SweepPage')),
      routeCache: 'SweepPage',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: BENCHMARK_PATHS,
      component: React.lazy(() => import('./pages/Benchmark/BenchmarkPage')),
      routeCache: 'BenchmarkPage',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    // {
    //   RouteComponent: PrivateRoute,
    //   path: `/:entityName/${PUBLISHED_WORK_PATH_SEGMENT}`,
    //   component: React.lazy(() => import('./pages/PublishedWorkPage')),
    //   routeCache: 'PublishedWorkPage',
    //   exact: true,
    //   allowAnonymous: true,
    // },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/reports',
      component: React.lazy(() => import('./pages/ReportPageEditOld')),
      routeCache: 'ReportPageEditOld',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/published',
      component: React.lazy(() => import('./pages/ReportPageViewOld')),
      routeCache: 'ReportPageViewOld',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/:entityName/:projectName/reports/:reportNameAndID/edit',
      component: React.lazy(() => import('./pages/ReportPageEdit')),
      routeCache: 'ReportPageEdit',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: REPORT_PAGE_VIEW_PATH,
      component: React.lazy(() => import('./pages/ReportPageView')),
      routeCache: 'ReportPageView',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
      component: React.lazy(() => import('./pages/ReportPageView')),
      routeCache: 'ReportPageViewPublishedWork',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: GALLERY_POST_EDIT_PATH,
      component: React.lazy(() => import('./pages/ReportPageEdit')),
      routeCache: 'ReportPageEdit',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: GALLERY_POST_VIEW_PATH,
      component: React.lazy(() => import('./pages/ReportPageView')),
      routeCache: 'ReportPageView',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: GALLERY_DISCUSSION_EDIT_PATH,
      component: React.lazy(() => import('./pages/ReportPageEdit')),
      routeCache: 'ReportPageEdit',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: GALLERY_DISCUSSION_VIEW_PATH,
      component: React.lazy(() => import('./pages/ReportPageView')),
      routeCache: 'ReportPageView',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: RouteWithLayout,
      path: '/:entityName/:projectName/querydemo',
      component: React.lazy(() => import('./pages/QueryDemoPage')),
      routeCache: 'QueryDemoPage',
      exact: true,
      allowAnonymous: true,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/org/:orgName',
      component: React.lazy(() => import('./pages/OrganizationPage')),
      routeCache: 'OrganizationPage',
      exact: true,
      allowAnonymous: false,
      condition: !isOnPrem,
    },
    {
      RouteComponent: PrivateRoute,
      path: '/repo/:repoName',
      component: React.lazy(() => import('./pages/RepoInsightsPage')),
      routeCache: 'RepoInsightsPage',
      exact: true,
      allowAnonymous: false,
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROJECT_PATHS,
      component: React.lazy(() => import('./pages/ProjectPage2')),
      routeCache: 'ProjectPage2',
      exact: true,
      allowIframes: true,
      allowAnonymous: true,
    },
    {
      // this is here to truncate extra path segments after the actual path
      path: PROJECT_BASE_PATH,
      redirect: PROJECT_BASE_PATH,
    },
    {
      RouteComponent: RouteWithLayout,
      path: PROFILE_PATH,
      component: React.lazy(() => import('./pages/EntityPage')),
      routeCache: 'EntityPage',
      exact: true,
      allowAnonymous: true,
    },
  ];

  // HAX: These checks ensure that the route mapping above is in sync with routeData.ts.
  // routeData.ts is shared between the main app and the host function.
  if (routes.length !== routesCheck.length) {
    throw new Error(
      `mismatched routes length: ${routes.length}, ${routesCheck.length}`
    );
  }
  for (let i = 0; i < routes.length; i++) {
    const r = routes[i];
    const rc = routesCheck[i];
    if (
      !_.isEqual(r.path, rc.path) ||
      r.redirect !== rc.redirect ||
      r.exact !== rc.exact
    ) {
      throw new Error(`mismatched routes: ${r.path}, ${rc.path}`);
    }
  }

  return routes;
}

const Routes: React.FC = makeComp(
  () => {
    const viewer = useViewer();

    return (
      <App>
        <GlobalNudgeBarContextProvider>
          <SearchNav />
          <StorageNudgeBar />
          <MessageOfTheDay />
          {envIsLocal && <GetLicenseNudgeBar />}
          {envIsLocal && <LocalLicenseNudgeBar />}
          {envIsLocal && viewer && <LocalNudgeBar />}
          {!envIsLocal && <ComputeHourNudgeBar />}
          {!envIsLocal && <TrialNudgeBar />}
          <div className="main">
            <ToastContainer />
            <React.Suspense fallback={<Loader />}>
              <Switch>
                {makeRoutes()
                  .filter(r => r.condition !== false)
                  .map((r, i) => {
                    if ('redirect' in r) {
                      return <Redirect key={i} from={r.path} to={r.redirect} />;
                    }
                    const {RouteComponent, condition, ...rest} = r;
                    const RC = RouteComponent as any;
                    return <RC key={i} {...rest} />;
                  })}
                <RouteWithLayout component={NoMatch} />
              </Switch>
            </React.Suspense>
          </div>
        </GlobalNudgeBarContextProvider>
      </App>
    );
  },
  {id: 'Routes'}
);

export default Routes;
